import React, { useEffect, useState } from 'react';
import { communityApi as community } from '../services/api';
import { HeartHandshake, Clock, ArrowRight, DollarSign } from 'lucide-react';

const LAUNCH_DATE = new Date('2027-04-01T00:00:00Z');
const FREE_ACCESS_ENDS = new Date('2027-03-31T23:59:59Z');
const GO_FUND_URL = 'https://gofund.me/2ac0082de';

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  return { days, hours, mins, secs, done: diff === 0 };
}

/** Pilot Funding + Donate section. Visible on the public homepage.
 *  Shows the KC MO pilot messaging, a live countdown to March 31 2027,
 *  the live "Total Cash on Reserve" number, and a GoFundMe donate CTA. */
export default function PilotFundingSection() {
  const cd = useCountdown(FREE_ACCESS_ENDS);
  const [reserve, setReserve] = useState({ totalCashOnReserve: 0.0 });

  useEffect(() => {
    let live = true;
    const load = () => community.reserveTotal().then(r => { if (live) setReserve(r); }).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { live = false; clearInterval(t); };
  }, []);

  return (
    <section className="max-w-5xl mx-auto px-4 py-10" data-testid="pilot-funding-section">
      <div className="rounded-3xl bg-gradient-to-br from-green-950 via-emerald-950 to-neutral-950 text-white p-6 md:p-10 shadow-2xl border border-green-500/20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-green-500/20 blur-3xl"/>
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-green-300 bg-green-500/10 border border-green-500/30 rounded-full px-3 py-1">
            <HeartHandshake size={11}/> Kansas City, MO · Pilot April 1, 2027
          </div>
          <h2 className="mt-4 text-2xl md:text-4xl font-black leading-tight">
            We are raising <span className="text-green-400">$100,000</span> to launch the Kansas City, Missouri pilot on April 1st, 2027.
          </h2>
          <p className="mt-3 text-neutral-300 text-sm md:text-base max-w-3xl">
            Every purchase and donation directly supports the build.
            <br/>
            <span className="text-green-300 font-semibold">All purchases now support the Kansas City, Missouri pilot. Get in free before launch.</span>
          </p>

          {/* Countdown */}
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="pilot-countdown">
            <Clock size={16} className="text-green-300"/>
            <div className="text-xs uppercase tracking-widest text-green-300 font-semibold mr-2">Free Access Ends</div>
            <div className="flex items-center gap-1">
              {[['Days', cd.days], ['Hrs', cd.hours], ['Min', cd.mins], ['Sec', cd.secs]].map(([lbl, val]) => (
                <div key={lbl} className="bg-white/10 rounded-lg px-3 py-2 border border-white/10 text-center min-w-[60px]"
                  data-testid={`pilot-cd-${lbl.toLowerCase()}`}>
                  <div className="text-xl md:text-2xl font-black leading-none tabular-nums">{String(val).padStart(2, '0')}</div>
                  <div className="text-[10px] uppercase text-neutral-400">{lbl}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Reserve stat + CTA */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-black/40 border border-white/10 p-5" data-testid="pilot-reserve-stat">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Total Cash on Reserve</div>
              <div className="text-xs text-neutral-500">Releasing April 1st, 2027</div>
              <div className="mt-2 text-3xl md:text-5xl font-black text-green-300 tabular-nums" data-testid="pilot-reserve-amount">
                ${reserve.totalCashOnReserve.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-5 flex flex-col justify-center">
              <div className="text-sm text-neutral-200">Support the mission directly through GoFundMe.</div>
              <a href={GO_FUND_URL} target="_blank" rel="noopener noreferrer"
                data-testid="pilot-donate-cta"
                className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-green-500 hover:bg-green-400 text-black font-bold shadow-lg">
                <DollarSign size={16}/> Support the Mission <ArrowRight size={14}/>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
